import { useCallback, useEffect, useRef, useState } from 'react'
import { errorMessage } from './api'

export interface Loaded<T> {
  data: T | null
  error: string | null
  loading: boolean
  /** Re-runs the fetch; quiet reloads keep the current data on screen. */
  reload: (opts?: { quiet?: boolean }) => Promise<void>
  /** Replaces the data locally (after an optimistic change, say). */
  setData: (updater: (prev: T | null) => T | null) => void
}

/**
 * Loads data for a screen. Re-runs when `deps` change, and polls every `pollMs` while the
 * tab is visible so a second staff member's changes show up without a refresh.
 */
export function useLoad<T>(fetcher: () => Promise<T>, deps: unknown[], pollMs?: number): Loaded<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  const generation = useRef(0)

  const reload = useCallback(async (opts: { quiet?: boolean } = {}) => {
    const gen = ++generation.current
    if (!opts.quiet) setLoading(true)
    try {
      const next = await fetcherRef.current()
      if (gen !== generation.current) return
      setData(next)
      setError(null)
    } catch (err) {
      if (gen !== generation.current) return
      setError(errorMessage(err))
    } finally {
      if (gen === generation.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    if (!pollMs) return
    const tick = () => {
      if (document.visibilityState === 'visible') void reload({ quiet: true })
    }
    const id = setInterval(tick, pollMs)
    document.addEventListener('visibilitychange', tick)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [pollMs, reload])

  return { data, error, loading, reload, setData: (u) => setData((prev) => u(prev)) }
}

export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return v
}

/** Wraps an async action with busy and error state for a button or form. */
export function useAction<A extends unknown[]>(fn: (...args: A) => Promise<void>) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const run = useCallback(
    async (...args: A) => {
      setBusy(true)
      setError(null)
      try {
        await fn(...args)
      } catch (err) {
        setError(errorMessage(err))
      } finally {
        setBusy(false)
      }
    },
    [fn],
  )
  return { run, busy, error, clearError: () => setError(null) }
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const mq = window.matchMedia(query)
    const onChange = () => setMatches(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [query])
  return matches
}
